/**
 * resolveLoadedTaxRate: which tax rate a quote shows when it is OPENED.
 *
 * Tiers (unchanged from QuoteBuilderPage's loader, extracted so they're testable):
 *   T0  sent / locked             -> the frozen stamped rate
 *   EX  exempt (IDI-380)          -> 0, never re-resolved from Settings
 *   T1  a stored per-quote rate   -> honour it
 *   T2-T4 resolve from Settings   -> site-state row, else default row, else 0
 *
 * IDI-380 (Urgent, 2026-09-22): an exempt quote's stored rate is 0, so it fell
 * through to T2-T4 and reloading put the dealer's 6% back on screen and into
 * the next autosave. Requirement: an exempt quote shows $0.00 tax every time
 * it's opened, until a user turns the exemption off; opening never changes tax.
 *
 * @returns {{ rate: number, needsSettings: boolean }} needsSettings=true means
 *   "call pickSettingsTaxRate with the tenant's rows".
 */
export function resolveLoadedTaxRate(quote) {
  const q = quote || {};
  const stored = Number(q.tax_rate ?? q.taxRate ?? 0) || 0;
  const locked = q.signatureStatus === 'acknowledged'
    || q.signatureStatus === 'acknowledged_pending'
    || q.status === 'sent';
  if (locked) return { rate: stored, needsSettings: false };
  if (q.tax_exempt ?? q.taxExempt) return { rate: 0, needsSettings: false };
  // IDI-668 (D3): a rate the user set in Edit Tax is theirs, 0% included.
  if ((q.tax_source ?? q.taxSource) === 'override') return { rate: stored, needsSettings: false };
  if (stored > 0) return { rate: stored, needsSettings: false };
  return { rate: 0, needsSettings: true };
}

/** T2-T4: site-state match, else the default row, else the first row, else 0. */
export function pickSettingsTaxRate(rows, quote) {
  const arr = Array.isArray(rows) ? rows : [];
  const q = quote || {};
  const siteState = (q.service_address?.state || q.billing_address?.state || '').toUpperCase();
  const stateMatch = siteState ? arr.find((r) => (r.state || '').toUpperCase() === siteState) : null;
  const matched = stateMatch || arr.find((r) => r.isDefault || r.is_default) || arr[0];
  if (!matched) return 0;
  return taxRowDecimal(matched);
}

/**
 * IDI-668: a Tax & Rates row's rate as a decimal. Rows store a percent
 * (8.25 means 8.25%); IDI-697: 1 or less is a percent too (1 → 0.01). 0 is no tax.
 */
export function taxRowDecimal(row) {
  const raw = Number(row?.rate ?? row?.tax_rate ?? 0) || 0;
  return raw > 0 ? raw / 100 : 0;
}

/** IDI-668: 0.0825 → "8.25", 0.045 → "4.5", 0.07 → "7". */
export function formatTaxPct(rate) {
  return String(Number((Number(rate || 0) * 100).toFixed(3)));
}

/**
 * IDI-668 (D5): the caption under the rail's Tax row, matched by value: the
 * site state's row, else the default row, else another state's row (a rate
 * picked from "Saved rate"), else "set on this quote". None at 0%.
 */
export function describeTaxRate(rows, rate, siteState) {
  if (!(rate > 0)) return null;
  const arr = Array.isArray(rows) ? rows : [];
  const same = (r) => !!r && Math.abs(taxRowDecimal(r) - rate) < 1e-9;
  const st = (r) => String(r?.state || '').trim().toUpperCase();
  const site = String(siteState || '').trim().toUpperCase();
  const siteRow = site ? arr.find((r) => st(r) === site) : null;
  const defRow = arr.find((r) => r.isDefault || r.is_default) || arr[0];
  const otherRow = arr.find((r) => st(r) && same(r));
  let why = 'set on this quote';
  if (same(siteRow)) why = `${site} rate`;
  else if (same(defRow)) why = 'your default tax rate';
  else if (otherRow) why = `${st(otherRow)} rate`;
  return `Taxed at ${formatTaxPct(rate)}% (${why})`;
}
